import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import AppLayout from '@/components/layout/AppLayout';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import LoginPage from '@/pages/Login/LoginPage';
import DebugLogPanel from '@/components/debug/DebugLogPanel';

// Lazy-loaded pages
const CompanyListPage = lazy(() => import('@/pages/Companies/CompanyListPage'));
const CompanyProfilePage = lazy(() => import('@/pages/Companies/CompanyProfilePage'));
const UserListPage = lazy(() => import('@/pages/Users/UserListPage'));
const SurveyTemplatePage = lazy(() => import('@/pages/Surveys/SurveyTemplatePage'));
const SurveyEditPage = lazy(() => import('@/pages/Surveys/SurveyEditPage'));
const CustomizeTemplatePage = lazy(() => import('@/pages/Surveys/CustomizeTemplatePage'));
const AiPromptsPage = lazy(() => import('@/pages/AI/AiPromptsPage'));
const AiTemplatePage = lazy(() => import('@/pages/AI/AiTemplatePage'));
const PromptFormPage = lazy(() => import('@/pages/AI/PromptFormPage'));
const TemplateFormPage = lazy(() => import('@/pages/AI/TemplateFormPage'));
const ApiLogsPage = lazy(() => import('@/pages/Logs/ApiLogsPage'));
const CustomerAccessLogsPage = lazy(() => import('@/pages/Logs/CustomerAccessLogsPage'));
const UsageStatsPage = lazy(() => import('@/pages/Stats/UsageStatsPage'));
const DevicesPage = lazy(() => import('@/pages/Stats/DevicesPage'));
const ProcessesPage = lazy(() => import('@/pages/Processes/ProcessesPage'));
const TemplateInfoPage = lazy(() => import('@/pages/Templates/TemplateInfoPage'));
const OnboardingPage = lazy(() => import('@/pages/Onboarding/OnboardingPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFound/NotFoundPage'));

const PageLoader = (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
    <Spin size="large" />
  </div>
);

export default function App() {
  return (
    <BrowserRouter>
      {import.meta.env.DEV && <DebugLogPanel />}
      <Suspense fallback={PageLoader}>
        <Routes>
          {/* Public routes */}
          <Route path="/signin" element={<LoginPage />} />

          {/* Protected routes with layout */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/companies" replace />} />
            <Route path="/companies" element={<CompanyListPage />} />
            <Route path="/companies/:companyId" element={<CompanyProfilePage />} />
            <Route path="/companies-list/:listType?" element={<CompanyListPage />} />
            <Route path="/users" element={<UserListPage />} />
            <Route path="/surveytemplates" element={<SurveyTemplatePage />} />
            <Route path="/surveytemplates/surveyedit/:surveyId" element={<SurveyEditPage />} />
            <Route path="/surveytheme/:surveyId" element={<SurveyEditPage />} />
            <Route
              path="/surveys/:surveyId/customize/:pageType"
              element={<CustomizeTemplatePage />}
            />
            <Route path="/ai-prompt" element={<AiPromptsPage />} />
            <Route path="/ai-prompt/add" element={<PromptFormPage />} />
            <Route path="/ai-prompt/edit/:id" element={<PromptFormPage />} />
            <Route path="/ai-template" element={<AiTemplatePage />} />
            <Route path="/ai-template/add" element={<TemplateFormPage />} />
            <Route path="/ai-template/edit/:templateId" element={<TemplateFormPage />} />
            <Route path="/ai-template/duplicate/:templateId" element={<TemplateFormPage />} />
            <Route path="/apilogs" element={<ApiLogsPage />} />
            <Route path="/customer-access-logs" element={<CustomerAccessLogsPage />} />
            <Route path="/usage-stats" element={<UsageStatsPage />} />
            <Route path="/devices" element={<DevicesPage />} />
            <Route path="/processes" element={<ProcessesPage />} />
            <Route path="/templateInfo" element={<TemplateInfoPage />} />
            <Route path="/onboardinginfo" element={<OnboardingPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
